/**
 * INYRON Life Biography / Offscreen Runtime
 *
 * Lightweight persistence layer for living entities. It does not own rendering,
 * transforms, navigation, spawning or AI decisions. It compresses life state when
 * an entity is offscreen and expands it again when the canonical consumer returns.
 */
export type BiographyLOD = "heroic" | "active" | "ambient" | "offscreen";
export type LifeStage = "juvenile" | "adult" | "elder" | "dead";
export interface BiographyEvent { id: string; at: number; kind: string; summary: string; salience: number; placeId?: string; actorIds: string[]; }
export interface BiographyRecord { entityId: string; speciesId: string; stage: LifeStage; ageSeconds: number; birthAt: number; deathAt?: number; lod: BiographyLOD; knownPlaces: string[]; knownIndividuals: string[]; habits: string[]; milestones: BiographyEvent[]; lastSimulatedAt: number; }

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
export class LifeBiographyRuntime {
  private readonly records = new Map<string, BiographyRecord>();
  private readonly offscreen = new Map<string, number>();

  register(entityId: string, speciesId: string, now = 0, ageSeconds = 0, initial?: Partial<BiographyRecord>) {
    const current = this.records.get(entityId);
    if (current) return current;
    const record: BiographyRecord = { entityId, speciesId, stage: initial?.stage ?? "adult", ageSeconds, birthAt: initial?.birthAt ?? Math.max(0, now - ageSeconds), lod: initial?.lod ?? "ambient", knownPlaces: initial?.knownPlaces ?? [], knownIndividuals: initial?.knownIndividuals ?? [], habits: initial?.habits ?? [], milestones: initial?.milestones ?? [], lastSimulatedAt: now };
    this.records.set(entityId, record); return record;
  }

  /** Compensate a never-committed registration without recording a death. */
  unregister(entityId: string): void { this.records.delete(entityId); this.offscreen.delete(entityId); }

  setLOD(entityId: string, lod: BiographyLOD, now: number) {
    const r = this.records.get(entityId); if (!r) return;
    r.lod = lod; if (lod === "offscreen") this.offscreen.set(entityId, now); else this.offscreen.delete(entityId); r.lastSimulatedAt = now;
  }

  advanceOffscreen(now: number) {
    for (const [id, since] of this.offscreen) {
      const r = this.records.get(id); if (!r || r.stage === "dead") continue;
      const dt = Math.max(0, now - Math.max(since, r.lastSimulatedAt)); if (!dt) continue;
      r.ageSeconds += dt; r.lastSimulatedAt = now; this.advanceStage(r, now);
    }
  }

  recordEvent(entityId: string, event: Omit<BiographyEvent, "id">) {
    const r = this.records.get(entityId); if (!r) return;
    const id = `${entityId}:${event.at}:${event.kind}:${event.summary}`.replace(/\s+/g, "_").slice(0, 200);
    if (r.milestones.some(m => m.id === id)) return;
    r.milestones.push({ ...event, id, salience: clamp01(event.salience) });
    r.milestones.sort((a, b) => a.at - b.at);
    if (r.milestones.length > 96) r.milestones.splice(0, r.milestones.length - 96);
    if (event.placeId && !r.knownPlaces.includes(event.placeId)) r.knownPlaces.push(event.placeId);
    for (const actor of event.actorIds) if (actor !== entityId && !r.knownIndividuals.includes(actor)) r.knownIndividuals.push(actor);
  }

  learnHabit(entityId: string, habit: string) { const r = this.records.get(entityId); if (!r || r.habits.includes(habit)) return; r.habits.push(habit); if (r.habits.length > 24) r.habits.shift(); }

  kill(entityId: string, now: number, summary = "life ended") { const r = this.records.get(entityId); if (!r || r.stage === "dead") return; r.stage = "dead"; r.deathAt = now; this.recordEvent(entityId, { at: now, kind: "death", summary, salience: 1, actorIds: [entityId] }); }

  get(entityId: string) { return this.records.get(entityId); }
  all() { return [...this.records.values()]; }
  snapshot() { return [...this.records.values()].map(r => ({ ...r, knownPlaces: [...r.knownPlaces], knownIndividuals: [...r.knownIndividuals], habits: [...r.habits], milestones: r.milestones.map(m => ({ ...m, actorIds: [...m.actorIds] })) })); }

  restore(snapshot: unknown): number {
    if (!Array.isArray(snapshot)) return 0;
    this.records.clear(); this.offscreen.clear();
    for (const raw of snapshot) {
      if (!raw || typeof raw !== "object") continue;
      const r = raw as Partial<BiographyRecord>;
      if (!r.entityId || !r.speciesId || !Number.isFinite(r.ageSeconds)) continue;
      const record = this.register(r.entityId, r.speciesId, Number(r.lastSimulatedAt ?? 0), Number(r.ageSeconds), r);
      if (record.lod === "offscreen") this.offscreen.set(record.entityId, record.lastSimulatedAt);
    }
    return this.records.size;
  }

  fastForward(elapsedSeconds: number, worldNowSeconds: number): number {
    if (!(elapsedSeconds > 0) || !Number.isFinite(elapsedSeconds)) return 0;
    for (const r of this.records.values()) {
      if (r.stage === "dead") continue;
      r.ageSeconds += elapsedSeconds; r.lastSimulatedAt = worldNowSeconds; this.advanceStage(r, worldNowSeconds);
    }
    return elapsedSeconds;
  }

  clear() { this.records.clear(); this.offscreen.clear(); }

  private advanceStage(r: BiographyRecord, now: number) {
    const age = r.ageSeconds;
    // Species-specific age curves belong to species authorities; these defaults only
    // provide a safe generic lifecycle state and are intentionally broad.
    if (age < 3600) r.stage = "juvenile";
    else if (age < 3600 * 24 * 365) r.stage = "adult";
    else r.stage = "elder";
    r.deathAt = undefined;
    r.lastSimulatedAt = now;
  }
}

export const lifeBiographyRuntime = new LifeBiographyRuntime();
